import React from 'react';
import { Clock, Globe, Share2, Check, Circle, BookOpen, Languages, Tv } from 'lucide-react';
import { StoryGroup, SimpleStory, getCanonicalStory } from '../services/simpleStore';
import { LANGUAGE_NAMES, LanguageCode, TV_FORMAT_SHORT_LABELS, TvFormat, formatDuration } from '../types';

interface StoryGroupCardProps {
  group: StoryGroup;
  onSelectLanguage: (storyId: string, language: LanguageCode) => void;
  onCreateTranslation?: (groupId: string, language: LanguageCode) => void;
  onGroupClick?: (group: StoryGroup) => void;
  authorName?: string; // Optional author name to display
}

// All supported languages in display order
const ALL_LANGUAGES: LanguageCode[] = ['eng', 'ps-AF', 'fa-AF'];

const StoryGroupCard: React.FC<StoryGroupCardProps> = ({
  group,
  onSelectLanguage,
  onCreateTranslation,
  onGroupClick,
  authorName,
}) => {
  const { primaryStory, availableLanguages, missingLanguages, stories } = group;
  
  // Find the canonical story using the helper function
  const canonicalStory = getCanonicalStory(group);

  // Check which stories have social posts by language
  const languagesWithSocialPosts = new Set(
    stories
      .filter(story => story.socialPosts &&
        (story.socialPosts.posts?.length > 0 || Object.keys(story.socialPosts).length > 0))
      .map(story => story.language)
  );

  // Get status priority (highest priority status from all versions)
  // Phase 3: Updated status values: draft, reviewed, approved, archived
  const statusPriority: Record<string, number> = {
    draft: 1,
    reviewed: 2,
    pending_review: 3,
    approved: 4,
    archived: 5,
  };

  const highestStatus = stories.reduce((highest, story) => {
    const currentPriority = statusPriority[story.status] || 99;
    const highestPriority = statusPriority[highest.status] || 99;
    return currentPriority > highestPriority ? story : highest;
  }, primaryStory);

  const statusLabel =
    highestStatus.status === 'reviewed'
      ? 'Reviewed'
      : highestStatus.status === 'pending_review'
      ? 'Pending Review'
      : highestStatus.status === 'approved'
      ? 'Approved'
      : highestStatus.status === 'archived'
      ? 'Archived'
      : 'Draft';

  const statusClasses = (() => {
    switch (highestStatus.status) {
      case 'reviewed':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'pending_review':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'approved':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'archived':
        return 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400';
      default:
        return 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400';
    }
  })();

  const handleCardClick = () => {
    // If only one language, open it directly
    if (availableLanguages.length === 1) {
      const story = stories.find(s => s.language === availableLanguages[0]);
      if (story) {
        onSelectLanguage(story.id, story.language);
      }
      return;
    }

    // Otherwise, show language selector (handled by parent)
    if (onGroupClick) {
      onGroupClick(group);
    } else {
      // Fallback: open primary story
      onSelectLanguage(primaryStory.id, primaryStory.language);
    }
  };

  const handleLanguageClick = (e: React.MouseEvent, lang: LanguageCode) => {
    e.stopPropagation();
    const story = stories.find(s => s.language === lang);
    if (story) {
      onSelectLanguage(story.id, lang);
    } else if (onCreateTranslation) {
      onCreateTranslation(group.groupId, lang);
    }
  };

  // Render a language status indicator
  const LanguageStatus = ({
    lang,
    isAvailable,
    hasSocial
  }: {
    lang: LanguageCode;
    isAvailable: boolean;
    hasSocial?: boolean;
  }) => {
    const name = LANGUAGE_NAMES[lang];

    if (isAvailable) {
      return (
        <button
          onClick={(e) => handleLanguageClick(e, lang)}
          className="flex items-center gap-1 text-xs hover:underline"
        >
          <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          <span className="text-zinc-700 dark:text-zinc-300">{name}</span>
        </button>
      );
    }

    return (
      <button
        onClick={(e) => handleLanguageClick(e, lang)}
        className="flex items-center gap-1 text-xs hover:underline opacity-50 hover:opacity-75"
        title={`Create ${name} translation`}
      >
        <Circle className="h-3.5 w-3.5 text-zinc-400 dark:text-zinc-500" />
        <span className="text-zinc-400 dark:text-zinc-500">{name}</span>
      </button>
    );
  };

  // Render social status for a language
  const SocialStatus = ({ lang }: { lang: LanguageCode }) => {
    const name = LANGUAGE_NAMES[lang];
    const isAvailable = availableLanguages.includes(lang);
    const hasSocial = languagesWithSocialPosts.has(lang);

    if (!isAvailable) {
      return (
        <div className="flex items-center gap-1 text-xs opacity-30">
          <Circle className="h-3.5 w-3.5 text-zinc-400" />
          <span className="text-zinc-400">{name}</span>
        </div>
      );
    }

    if (hasSocial) {
      return (
        <div className="flex items-center gap-1 text-xs">
          <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          <span className="text-zinc-700 dark:text-zinc-300">{name}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 text-xs opacity-50">
        <Circle className="h-3.5 w-3.5 text-zinc-400 dark:text-zinc-500" />
        <span className="text-zinc-400 dark:text-zinc-500">{name}</span>
      </div>
    );
  };

  return (
    <button
      onClick={handleCardClick}
      className="w-full text-left p-4 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-200 active:scale-[0.99] group"
    >
      {/* Header row: Title + Status badge */}
      <div className="flex items-start gap-3 mb-3">
        <h3 className="flex-1 font-medium text-zinc-900 dark:text-white line-clamp-2 leading-snug">
          {primaryStory.platformMetadata.slug || primaryStory.headline || 'Untitled'}
        </h3>
        <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${statusClasses}`}>
          {statusLabel}
        </span>
      </div>

      {/* Deck/summary for web OR Duration for TV */}
      {primaryStory.platform === 'tv' ? (
        // TV story: show duration or anchor intro preview
        primaryStory.platformMetadata.durationEstimate ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 mb-3">
            <span className="text-base">⏱</span>
            <span>{formatDuration(primaryStory.platformMetadata.durationEstimate)}</span>
            {primaryStory.platformMetadata.durationTarget && (
              <span className="text-zinc-400 dark:text-zinc-500">
                / {formatDuration(primaryStory.platformMetadata.durationTarget)}
              </span>
            )}
          </div>
        ) : primaryStory.platformMetadata.anchorIntro ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-1 mb-3 italic">
            {primaryStory.platformMetadata.anchorIntro}
          </p>
        ) : primaryStory.deck ? (
          // Fallback to deck for legacy TV stories
          <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-1 mb-3">
            {primaryStory.deck}
          </p>
        ) : null
      ) : (
        // Web story: show deck/summary
        primaryStory.deck && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-1 mb-3">
            {primaryStory.deck}
          </p>
        )
      )}

      {/* Status rows */}
      <div className="space-y-1.5 pt-2 border-t border-zinc-100 dark:border-zinc-800">
        {/* Platform row - shows TV or Web based on story platform */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 w-16">
            {primaryStory.platform === 'tv' ? (
              <>
                <Tv className="h-3.5 w-3.5" />
                <span>TV</span>
              </>
            ) : (
              <>
                <Globe className="h-3.5 w-3.5" />
                <span>Web</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {ALL_LANGUAGES.map(lang => {
              const story = stories.find(s => s.language === lang);
              const isCanon = story && story.id === canonicalStory?.id;
              const hasBadge = story && (isCanon || !isCanon);
              return (
                <div key={lang} className="flex items-center gap-1.5 w-[120px]">
                  <div className="flex items-center gap-1">
                    <LanguageStatus
                      lang={lang}
                      isAvailable={availableLanguages.includes(lang)}
                    />
                  </div>
                  <div className="w-[70px] flex items-center gap-1">
                    {isCanon && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded whitespace-nowrap"
                        title="Canonical (original) story"
                      >
                        <BookOpen className="h-2.5 w-2.5" />
                        Canon
                      </span>
                    )}
                    {story && !isCanon && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded whitespace-nowrap"
                        title="Translation"
                      >
                        <Languages className="h-2.5 w-2.5" />
                        Trans
                      </span>
                    )}
                    {/* TV Format badge */}
                    {story && story.platform === 'tv' && story.platformMetadata.tvFormat && (
                      <span
                        className="inline-flex items-center px-1 py-0.5 text-[9px] font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded whitespace-nowrap"
                        title={`TV Format: ${TV_FORMAT_SHORT_LABELS[story.platformMetadata.tvFormat as TvFormat]}`}
                      >
                        {TV_FORMAT_SHORT_LABELS[story.platformMetadata.tvFormat as TvFormat]}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Social row */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 w-16">
            <Share2 className="h-3.5 w-3.5" />
            <span>Social</span>
          </div>
          <div className="flex items-center gap-4">
            {ALL_LANGUAGES.map(lang => (
              <div key={lang} className="flex items-center gap-1.5 w-[120px]">
                <div className="flex items-center gap-1">
                  <SocialStatus lang={lang} />
                </div>
                <div className="w-[50px]"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timestamp and Author */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-500">
          <Clock className="h-3 w-3" />
          <span>{new Date(primaryStory.updatedAt || primaryStory.createdAt).toLocaleDateString()}</span>
        </div>
        {authorName && (
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            By {authorName}
          </div>
        )}
      </div>
    </button>
  );
};

export default StoryGroupCard;
